/*
  Signature Product Interview
  - Teaser video is paused on load (NO autoplay). It plays only when the user taps “Tap to play”.
  - Custom controls overlay (play/pause/stop/mute/volume) — shown after user interaction.
  - Full film modal (with VTT subtitle language auto-selection by market language).
  - When one video starts playing, all other sigpi videos stop (prevents multiple videos playing at once).
*/

(() => {
  const SEL = {
    root: '[data-sigpi]',
    teaser: 'video[data-role="teaser"]',
    full: 'video[data-role="full"]',
    tapBtn: '[data-action="tap-to-play"]',
    openBtn: '[data-action="open-modal"]',
    closeBtn: '[data-action="close-modal"]',
    modal: '[data-role="modal"]',
    i18n: 'script[data-sigpi-i18n]'
  };

  // Stop all other videos in this component so multiple videos never play at once.
  // (Important on product pages where multiple sections can exist.)
  const stopAllSigpiVideosExcept = (exceptVideo) => {
    document.querySelectorAll('video.sigpi__video').forEach((v) => {
      if (exceptVideo && v === exceptVideo) return;

      // Stop + reset
      if (v.dataset?.role === 'teaser') {
  resetTeaserToPoster(v);
} else {
  stopVideo(v);
}


      // If it's a teaser, return to the "Tap to play" initial state
      if (v.dataset?.role === 'teaser') {
        try { v.muted = true; } catch (_) {}
        try { v.volume = 0; } catch (_) {}
        const r = v.closest?.(SEL.root);
        if (r) r.classList.remove('sigpi--userPlaying');
      }

      // Sync custom controls UI if possible
      const root = v.closest?.(SEL.root);
      if (root) {
        const target = v.dataset?.role === 'full' ? 'full' : 'teaser';
        setControlsState(getControlsEl(root, target), v);
      }
    });
  };

  // Helpers
  const parseI18n = (root) => {
    try {
      const el = root.querySelector(SEL.i18n);
      if (!el) return { marketLanguage: 'en', subtitleCandidates: ['en', 'ko', 'ja', 'fr'] };
      return JSON.parse(el.textContent || '{}');
    } catch (e) {
      return { marketLanguage: 'en', subtitleCandidates: ['en', 'ko', 'ja', 'fr'] };
    }
  };

  const safePlay = async (video) => {
    try {
      const p = video.play();
      if (p && typeof p.then === 'function') await p;
    } catch (_) {}
  };
 // Teaser를 "포스터 상태"로 확실히 되돌리는 함수
const resetTeaserToPoster = (video) => {
  if (!video) return;
  try { video.pause(); } catch (_) {}
  try { video.muted = true; } catch (_) {}
  try { video.volume = 0; } catch (_) {}
  try { video.currentTime = 0; } catch (_) {}

  // ★ 이게 핵심: poster가 다시 보이도록 강제 리셋
  try { video.load(); } catch (_) {}
};

  const safePause = (video) => {
    try { video.pause(); } catch (_) {}
  };

  const stopVideo = (video) => {
    try { video.pause(); } catch (_) {}
    try { video.currentTime = 0; } catch (_) {}
  };

  const setVolume = (video, v) => {
    try { video.volume = Math.min(1, Math.max(0, v)); } catch (_) {}
    try { video.muted = (video.volume === 0); } catch (_) {}
  };

  const getControlsEl = (root, target) => root.querySelector(`.sigpi__controls[data-target="${target}"]`);

  const setControlsState = (controlsEl, video) => {
    if (!controlsEl || !video) return;
    const isPlaying = !video.paused && !video.ended;
    const isMuted = video.muted || video.volume === 0;

    controlsEl.dataset.playing = String(isPlaying);
    controlsEl.dataset.muted = String(isMuted);

    const range = controlsEl.querySelector('input[type="range"][data-action="volume"]');
    if (range) range.value = String(video.volume ?? 0);
  };

  // Subtitle selection based on market language
  const applySubtitlePreference = (video, candidates = []) => {
    if (!video) return;

    const tracks = Array.from(video.textTracks || []);
    if (!tracks.length) return;

    // Default all off; then enable best match
    tracks.forEach((t) => { t.mode = 'disabled'; });

    const trackEls = Array.from(video.querySelectorAll('track[kind="subtitles"]'));
    const byLang = new Map();

    trackEls.forEach((el) => {
      const lang = (el.getAttribute('srclang') || '').toLowerCase();
      if (!lang) return;
      byLang.set(lang, el);
    });

    const preferred = (candidates || []).map((c) => String(c || '').toLowerCase()).filter(Boolean);

    const selectedLang = preferred.find((lang) => byLang.has(lang)) || (byLang.has('en') ? 'en' : null);
    if (!selectedLang) return;

    // Enable matching textTrack
    const match = tracks.find((t) => (t.language || '').toLowerCase() === selectedLang);
    if (match) match.mode = 'showing';
  };

  // Ensure teaser is audible after user gesture (tap button)
  const ensureAudibleDefaults = (video, controlsEl, { defaultVolume = 0.75 } = {}) => {
    if (!video) return;

    // If already audible, nothing
    const audible = !(video.muted || video.volume === 0);
    if (audible) return;

    try { video.muted = false; } catch (_) {}
    setVolume(video, defaultVolume);

    // Sync slider
    setControlsState(controlsEl, video);
  };

  const bindControls = (root, target, videoEl) => {
    const controlsEl = getControlsEl(root, target);
    if (!controlsEl || !videoEl) return;

    // Initial sync
    setControlsState(controlsEl, videoEl);

    // Click actions (buttons)
    controlsEl.addEventListener('click', (e) => {
      const btn = e.target.closest?.('[data-action]');
      if (!btn) return;

      const action = btn.getAttribute('data-action');
      if (!action) return;

      if (action === 'toggle-play') {
        if (videoEl.paused) {
          // If teaser: if user starts play from controls, show controls & allow sound (but keep volume behavior)
          if (target === 'teaser') root.classList.add('sigpi--userPlaying');
          stopAllSigpiVideosExcept(videoEl);
          safePlay(videoEl);
        } else {
          safePause(videoEl);
        }
        setControlsState(controlsEl, videoEl);
        return;
      }

      if (action === 'stop') {
        stopVideo(videoEl);

        // For teaser: revert to initial paused state + show Tap-to-play again
        if (target === 'teaser') {
          try { videoEl.muted = true; } catch (_) {}
          setVolume(videoEl, 0);
          root.classList.remove('sigpi--userPlaying');
        }

        setControlsState(controlsEl, videoEl);
        return;
      }

      if (action === 'toggle-mute') {
        const nowMuted = !(videoEl.muted || videoEl.volume === 0) ? true : false;
        if (nowMuted) {
          // mute
          try { videoEl.muted = true; } catch (_) {}
          setVolume(videoEl, 0);
        } else {
          // unmute to a safe level
          try { videoEl.muted = false; } catch (_) {}
          const v = (videoEl.volume && videoEl.volume > 0) ? videoEl.volume : 0.75;
          setVolume(videoEl, v);
        }
        setControlsState(controlsEl, videoEl);
        return;
      }

      // Volume slider handled in input event
    });

    // Range input
    const range = controlsEl.querySelector('input[type="range"][data-action="volume"]');
    if (range) {
      range.addEventListener('input', () => {
        const v = parseFloat(range.value);
        setVolume(videoEl, isNaN(v) ? 0 : v);
        // If user moves volume above 0, consider as user playing state (for teaser)
        if (target === 'teaser' && (videoEl.volume > 0)) root.classList.add('sigpi--userPlaying');
        setControlsState(controlsEl, videoEl);
      });
    }

    // Sync when play/pause ends
    ['play', 'pause', 'ended', 'volumechange'].forEach((evt) => {
      videoEl.addEventListener(evt, () => setControlsState(controlsEl, videoEl));
    });
  };

  const lockScroll = (lock) => {
    const cls = 'sigpi--scrolllock';
    if (lock) document.documentElement.classList.add(cls);
    else document.documentElement.classList.remove(cls);
  };

  const openModal = (root) => {
    const modal = root.querySelector(SEL.modal);
    if (!modal) return;

    // Stop teaser (don’t keep playing behind modal)
    const teaser = root.querySelector(SEL.teaser);
    if (teaser) {
      safePause(teaser);
    // stopVideo(teaser);  // <- 이 줄 제거(또는 주석)
      try { teaser.muted = true; } catch (_) {}
      try { teaser.volume = 0; } catch (_) {}
      root.classList.remove('sigpi--userPlaying');
      setControlsState(getControlsEl(root, 'teaser'), teaser);
    }

    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    root.classList.add('sigpi--modalOpen');
    lockScroll(true);

    // Subtitle selection for full video also
   const i18n = parseI18n(root);
const full = root.querySelector(SEL.full);

if (full) {
  applySubtitlePreference(full, i18n.subtitleCandidates);

  // ★ 핵심: 모달 열릴 때 바로 재생 (클릭 제스처 안에서 실행)
  requestAnimationFrame(() => {
    safePlay(full);
  });
}

  };

  const closeModal = (root) => {
    const modal = root.querySelector(SEL.modal);
    if (!modal) return;

    const full = root.querySelector(SEL.full);
if (full) {
  stopVideo(full);
  setControlsState(getControlsEl(root, 'full'), full);
}


    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    root.classList.remove('sigpi--modalOpen');
    lockScroll(false);

    // Reset teaser to initial paused state (NO autoplay)
 // Reset teaser to initial paused poster state (NO autoplay)
const teaser = root.querySelector(SEL.teaser);
if (teaser) {
  // 혹시라도 autoplay 속성이 생겼을 가능성까지 차단
  try { teaser.autoplay = false; } catch (_) {}
  teaser.removeAttribute?.('autoplay');

  root.classList.remove('sigpi--userPlaying');
  resetTeaserToPoster(teaser); // ★ poster로 강제 복귀 (재생 X)
  setControlsState(getControlsEl(root, 'teaser'), teaser);
}

  };

  const bindSection = (root) => {
    if (!root || root.__sigpiBound) return;
    root.__sigpiBound = true;

    const i18n = parseI18n(root);
    const candidates = i18n.subtitleCandidates || [i18n.marketLanguage, 'en', 'ko', 'ja', 'fr'];

    // Teaser init: paused at first frame (NO autoplay — plays only after user taps)
    const teaser = root.querySelector(SEL.teaser);
    if (teaser) {
      applySubtitlePreference(teaser, candidates);

      // Ensure initial paused state (muted, at time 0, waiting for tap-to-play)
      try { teaser.muted = true; } catch (_) {}
      try { teaser.volume = 0; } catch (_) {}
      root.classList.remove('sigpi--userPlaying');
      stopVideo(teaser);
      setControlsState(getControlsEl(root, 'teaser'), teaser);

      // Bind custom teaser controls
      bindControls(root, 'teaser', teaser);

      // Tap-to-play button enables sound + starts play
      const tapBtn = root.querySelector(SEL.tapBtn);
      if (tapBtn) {
        tapBtn.addEventListener('click', () => {
          stopAllSigpiVideosExcept(teaser);
          ensureAudibleDefaults(teaser, getControlsEl(root, 'teaser'), { defaultVolume: 0.75 });
          safePlay(teaser);
          root.classList.add('sigpi--userPlaying');
          setControlsState(getControlsEl(root, 'teaser'), teaser);
        });
      }
    }

    // Open modal (CTA)
    const openBtn = root.querySelector(SEL.openBtn);
    if (openBtn) {
      openBtn.addEventListener('click', () => {
        stopAllSigpiVideosExcept(null);
        openModal(root);
      });
    }

    // Close modal (X, backdrop etc.) — supports click on backdrop
    const modal = root.querySelector(SEL.modal);
    if (modal) {
      modal.addEventListener('click', (e) => {
        const close = e.target.closest?.(SEL.closeBtn);
        if (close) closeModal(root);
      });

      // ESC key closes modal
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !modal.hidden) closeModal(root);
      });
    }

    // Full video controls state (native controls exist, but we keep dataset updated if needed)
    const full = root.querySelector(SEL.full);
    if (full) {
      applySubtitlePreference(full, candidates);
      // No custom controls for full; keep default controls
      ['play', 'pause', 'ended', 'volumechange'].forEach((evt) => {
        full.addEventListener(evt, () => setControlsState(getControlsEl(root, 'full'), full));
      });
    }
  };

  // Bind all
  const init = () => {
    document.querySelectorAll(SEL.root).forEach(bindSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
